import { MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Box } from "../../../common/components";
import { Input } from "../../../common/components/Input";
import { Select } from "../../../common/components/Select/Select";
import { spacing } from "../../../styles/theme";
import { AddressFormType, AddressTypeEnum, LANGUAGE } from "../../../types";
import CAProvinces from "../provinces.json";
import USStates from "../states.json";
import { createStyles } from "../../../styles/theme";
import InputGooglePlaces from "../../../common/components/Input/InputGooglePlaces";

const styles = createStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: { xs: spacing.groupingMobile, lg: spacing.groupingDesktop },
  },
  sharedRow: {
    display: "flex",
    flexDirection: { xs: "column", lg: "row" },
    alignItems: "center",
    gap: "12px",
    width: "100%",
    "&>div": {
      maxWidth: "unset",
    },
  },
});

interface Props {
  address: AddressFormType;
  onAddressFieldChange: (e: any, field: keyof AddressFormType) => void;
  onAddressCountryChange: (e: any) => void;
  autoCompletePrefix?: "shipping" | "billing";
  "data-test"?: string;
  addressType?: AddressTypeEnum;
}

const AddressForm = (props: Props) => {
  const { t } = useTranslation();
  const {
    address,
    onAddressFieldChange,
    onAddressCountryChange,
    autoCompletePrefix = "",
    "data-test": dataTest,
    addressType = AddressTypeEnum.SHIPPING,
  } = props;

  const getAutoComplete = (field: string) => {
    return autoCompletePrefix ? `${autoCompletePrefix} ${field}` : field;
  };

  return (
    <Box sx={styles.container} data-test={dataTest}>
      <Box sx={styles.sharedRow}>
        <Input
          name="firstName"
          data-test={`${dataTest}.firstName`}
          value={address.firstName}
          onChange={(e) => onAddressFieldChange(e, "firstName")}
          label={t("checkout.first_name")}
          autoComplete={getAutoComplete("given-name")}
          required
        />
        <Input
          name="lastName"
          data-test={`${dataTest}.lastName`}
          value={address.lastName}
          onChange={(e) => onAddressFieldChange(e, "lastName")}
          label={t("checkout.last_name")}
          autoComplete={getAutoComplete("family-name")}
          required
        />
      </Box>
      <Box sx={styles.sharedRow}>
        <Input
          name="email"
          data-test={`${dataTest}.email`}
          value={address.email}
          onChange={(e) => onAddressFieldChange(e, "email")}
          label={t("checkout.email")}
          subLabel={
            addressType === "shipping"
              ? t("checkout.for_delivery_notifications")
              : undefined
          }
          autoComplete={getAutoComplete("email")}
          type={"email"}
          required
        />
        <Input
          key="phone"
          data-test={`${dataTest}.phone`}
          value={address.phone}
          onChange={(e) => onAddressFieldChange(e, "phone")}
          label={t("general.phone")}
          subLabel={
            addressType === "shipping"
              ? t("checkout.for_delivery_notifications")
              : undefined
          }
          autoComplete={getAutoComplete("tel")}
          required
        />
      </Box>
      {addressType === "shipping" && (
        <Select
          key="communicationLanguage"
          name="communicationLanguage"
          data-test={`${dataTest}.communicationLanguage`}
          onChange={(e: any) =>
            onAddressFieldChange(e, "communicationLanguage")
          }
          value={address.communicationLanguage}
          label={t("checkout.communication_language")}
          autoComplete={getAutoComplete("communicationLanguage")}
          fullWidth
          controlled
        >
          {Object.values(LANGUAGE).map((option) => (
            <MenuItem
              value={option}
              key={option}
              data-test={`${dataTest}.communicationLanguage.${option}`}
            >
              {t(`general.language.${option}`)}
            </MenuItem>
          ))}
        </Select>
      )}
      <Input
        name="organization"
        data-test={`${dataTest}.organization`}
        value={address.organization}
        onChange={(e) => onAddressFieldChange(e, "organization")}
        label={t("checkout.organization")}
        autoComplete={getAutoComplete("organization")}
        required
        fullWidth
      />
      <Select
        key="country"
        name="country"
        data-test={`${dataTest}.country`}
        onChange={(e: any) => onAddressCountryChange(e)}
        value={address.country}
        label={t("checkout.country")}
        autoComplete={getAutoComplete("country")}
        fullWidth
        controlled
      >
        {["CA", "US"].map((option) => (
          <MenuItem
            value={option}
            key={option}
            data-test={`${dataTest}.country.${option}`}
          >
            {t(`general.country.${option}`)}
          </MenuItem>
        ))}
      </Select>
      <InputGooglePlaces
        value={address.address}
        onChange={(value) =>
          onAddressFieldChange({ target: { value } }, "address")
        }
        onPlaceSelected={(place) => {
          onAddressFieldChange({ target: { value: place.address } }, "address");
          onAddressFieldChange(
            { target: { value: place.appartment } },
            "appartment"
          );
          onAddressFieldChange({ target: { value: place.city } }, "city");
          onAddressFieldChange({ target: { value: place.state } }, "state");
          onAddressFieldChange({ target: { value: place.zipcode } }, "zipcode");
        }}
        country={address.country}
        label={t("checkout.addressLine1")}
        data-test={`${dataTest}.address`}
      />
      <Input
        name="appartment"
        data-test={`${dataTest}.appartment`}
        value={address.appartment}
        onChange={(e) => onAddressFieldChange(e, "appartment")}
        label={t("checkout.addressLine2")}
        autoComplete={getAutoComplete("address-line2")}
        fullWidth
      />
      <Input
        name="city"
        data-test={`${dataTest}.city`}
        value={address.city}
        onChange={(e) => onAddressFieldChange(e, "city")}
        label={t("checkout.city")}
        autoComplete={getAutoComplete("address-level2")}
        required
        fullWidth
      />
      <Box sx={styles.sharedRow}>
        <Select
          key="state"
          data-test={`${dataTest}.state`}
          value={address.state}
          onChange={(e: any) => onAddressFieldChange(e, "state")}
          variant="outlined"
          controlled
          label={
            address.country === "US"
              ? t("checkout.state")
              : t("checkout.province_region")
          }
          autoComplete={getAutoComplete("address-level1")}
        >
          {(address.country === "US" ? USStates : CAProvinces).map((option) => (
            <MenuItem
              value={option}
              key={option}
              data-test={`${dataTest}.state.${option}`}
            >
              {t(`alphaCodes.${option}`)}
            </MenuItem>
          ))}
        </Select>
        <Input
          name="zipcode"
          data-test={`${dataTest}.zipcode`}
          value={address.zipcode}
          onChange={(e) => onAddressFieldChange(e, "zipcode")}
          label={
            address.country === "US"
              ? t("checkout.zip_code")
              : t("checkout.postal_code")
          }
          autoComplete={getAutoComplete("postal-code")}
          required
        />
      </Box>
    </Box>
  );
};

export default AddressForm;
